import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useTranslation } from 'react-i18next'
import { ChevronRight } from 'lucide-react'
import { AnimatePresence, motion, type TargetAndTransition } from 'framer-motion'
import { TaskCheck } from '../TaskCheck'
import { TaskAttentionBadge } from '../TaskAttentionBadge'
import { TaskStartDateBadge } from '../TaskStartDateBadge'
import { TaskDeadlineBadge } from '../TaskDeadlineBadge'
import { TaskDurationBadge } from '../TaskDurationBadge'
import { TaskTagsBadge } from '../TaskTagsBadge'
import { useTags } from '../../hooks/useTags'
import { RewardCatalogIcon } from '@/src/renderer/components/ui/RewardCatalogIcon'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'
import { getRewardById, rewardCta, type CatalogLanguage } from '@/src/renderer/lib/data/rewardCatalog'
import { RecurringIcon } from '@/src/renderer/components/icons'
import { describeRecurrence, shortRecurrenceLabel } from '@/src/renderer/lib/recurrence'
import { BranchProgress } from '../BranchProgress'
import { CommentMark } from '../CommentMark'
import { ReminderMark } from '../ReminderMark'
import { useOwnReminderLabels } from '../../hooks/useOwnReminderLabels'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { growMotion } from '@/src/renderer/lib/motion/growMotion'
import type { SubtaskRowProps } from './SubtaskList.types'
import { SubtaskTreeLines, treeRowClasses } from './SubtaskTreeLines'
import { TASK_MENU_REST_KEYS, matchesTaskKey, taskMenuTargetForKey } from '../TaskMenu/taskMenuShortcuts'

/** Ein einzelnes Zeichen, das getippt wurde — nicht Tab, Escape oder F5. */
function typedCharacter(e: React.KeyboardEvent): string | null {
  if (e.key.length !== 1 || e.metaKey || e.ctrlKey || e.altKey) return null
  // Die Leertaste ist vergeben: Sie hakt ab (wie in der Aufgabenliste).
  if (e.key === ' ') return null
  return e.key
}

/*
  **Die Marken gehen beim Schreiben weich** (Sven, 04.10.2026): `growMotion` im Takt der Breadcrumb.
  Beim Öffnen geht erst der Inhalt, dann der Platz; beim Schließen umgekehrt. Der Titel rückt mit,
  statt zu springen.

  - Der `gap` der Zeile (8 px, `--spacing-2`) fällt mit — als negativer Rand, der mit der Breite
    läuft. Sonst stieße der Titel am Ende um 8 px je Kasten nach (dieselbe Lösung wie in `SidebarItem`).
  - `overflow: hidden` nur während der Bewegung: In Ruhe schnitte es Fokus-Ring und Hover der Marken ab.
*/
const ROW_GAP = 8
const grow = growMotion({ axis: 'width', size: 'auto', content: 0.2, enterDelay: 0.05, exitDelay: 0.1 })
const widthTransition = (target: TargetAndTransition) => (target.transition as Record<string, unknown>).width
const markGrow = {
  initial: { ...grow.initial, marginRight: -ROW_GAP, overflow: 'hidden' },
  animate: {
    ...grow.animate,
    marginRight: 0,
    transition: { ...grow.animate.transition, marginRight: widthTransition(grow.animate) },
    transitionEnd: { overflow: 'visible' },
  },
  exit: {
    ...grow.exit,
    marginRight: -ROW_GAP,
    overflow: 'hidden',
    transition: { ...grow.exit.transition, marginRight: widthTransition(grow.exit) },
  },
} as Pick<typeof grow, 'initial' | 'animate' | 'exit'>

/**
 * Die Tags der Zeile mit Namen (#218). Eigener Baustein, damit nur Zeilen MIT Tags die Liste der
 * Tags abfragen. Ein gelöschter Tag fällt still heraus.
 */
const SubtaskRowTags: React.FC<{ ids: string[] }> = ({ ids }) => {
  const { tags } = useTags()
  const names = useMemo(
    () => ids.flatMap(id => tags.find(tag => tag.id === id)?.name ?? []),
    [ids, tags],
  )
  return <TaskTagsBadge names={names} />
}

/**
 * One checklist row: tick, title, and a remove button that appears on hover —
 * the same affordance the property badges use.
 *
 * **Sie hat zwei Zustände** (Nutzer-Vorgabe, 2026-09-20), wie eine **Aufgabe**
 * in der Liste und ein Zettel auf der **Pinnwand**:
 *
 * - **ausgewählt** — ein Klick, getönte Fläche, kein Caret. Die Tasten
 *   gehören der Liste: Pfeile bewegen, Leertaste hakt ab, Entf löscht.
 * - **bearbeitend** — Doppelklick, Enter oder einfach lostippen. Weißes Feld mit
 *   farbigem Rahmen und Ring, genau wie `Input` im Fokus.
 *
 * Der Titel wird beim Schließen geschrieben, nicht bei jedem Anschlag: eine
 * Änderung ist ein Schreibvorgang und ein Sync-Eintrag, nicht einer je Zeichen.
 *
 * **Die ganze Zeile ist der Ziehgriff** — außer der offenen; darum kümmert sich
 * der Sensor in `SubtaskList`. Ohne Maus verschiebt Alt+↑/↓.
 */
export const SubtaskRow: React.FC<SubtaskRowProps> = ({
  item, selected, editing, onToggle, asked, sparkDelay, onRename, onRemove, onSelect, onRelease, onEdit, onEndEdit,
  onOpenBelow, onNudge, onStep, onToggleSelection, onRemoveSelection,
  registerInput, focusPrevious, focusNext, onOpen, onOpenComments, onOpenReminders, away, onUnnest, onMenuShortcut, onRestShortcut, onDuplicate,
  lines, onLevel,
}) => {
  const { t, i18n } = useTranslation()
  const commentCount = useDataSource().useCommentCount(item.id)
  const reminderLabels = useOwnReminderLabels(item.id)
  const language: CatalogLanguage = i18n.language?.startsWith('en') ? 'en' : 'de'
  const reward = getRewardById(item.rewardId)
  const [title, setTitle] = useState(item.title)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const progress = item.progress
  const hasChildren = (lines?.stem ?? false) || (progress?.total ?? 0) > 0

  /**
   * Ob der Druck die Auswahl schon bestimmt hat.
   *
   * `pointerdown` feuert VOR `focus`. Ohne diesen Merker riefe der Fokus gleich
   * danach `onSelect` ohne Tasten hinterher — und ein Cmd-Klick, der gerade eine
   * zweite Zeile dazugenommen hat, fiele im selben Atemzug wieder auf eine
   * zusammen.
   */
  const pointerDecided = useRef(false)

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: editing,
    animateLayoutChanges: () => false,
  })

  // Follow the stored value when it changes underneath us — a sync pull, or the
  // same task open in another window.
  useEffect(() => { setTitle(item.title) }, [item.title])

  useEffect(() => {
    registerInput(item.id, inputRef.current)
    return () => registerInput(item.id, null)
  }, [item.id, registerInput])

  /*
    Der Caret springt ans ENDE, sobald die Zeile aufgeht — bei allen drei Wegen
    hinein gleich (Doppelklick, Enter, Lostippen). Ein markierter Titel wäre der
    vierte Zustand und der einzige, in dem das nächste Zeichen alles wegnimmt.
  */
  useEffect(() => {
    if (!editing) return
    const el = inputRef.current
    if (!el) return
    el.focus()
    const ende = el.value.length
    el.setSelectionRange(ende, ende)
  }, [editing])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Alt+↑/↓ verschiebt die Zeile — in beiden Zuständen, damit man beim
    // Schreiben nicht erst schließen muss.
    // ⌥↑/↓ verschiebt, ⇧⌥↑/↓ löst heraus — über bzw. unter die Aufgabe (#204, Unter-Plan
    // Tastatur). In beiden Zuständen, damit man beim Schreiben nicht erst schließen muss.
    if (e.altKey && !e.metaKey && !e.ctrlKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault()
      e.stopPropagation()
      if (!e.shiftKey) onNudge(item.id, e.key === 'ArrowDown' ? 1 : -1)
      else onUnnest?.(item.id, e.key === 'ArrowUp' ? 'above' : 'below')
      return
    }

    /*
      **⌥→ Einrücken, ⌥← Ausrücken** (#181) — nur an der angefassten Zeile. Beim Schreiben bleiben
      die Tasten das Springen um ein Wort (macOS).
    */
    if (!editing && onLevel && e.altKey && !e.shiftKey && !e.metaKey && !e.ctrlKey
      && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
      e.preventDefault()
      e.stopPropagation()
      onLevel(item.id, e.key === 'ArrowRight' ? 'indent' : 'outdent')
      return
    }

    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      // Cmd/Ctrl+Enter schiebt eine offene Zeile darunter — dieselbe Taste, mit
      // der die Aufgabenliste „speichern und darunter eine neue anlegen" meint
      // (`TaskEntry`). Das `stopPropagation` ist Pflicht: Ohne es hörte die
      // Karte mit und legte eine neue AUFGABE an.
      if (e.metaKey || e.ctrlKey) {
        if (title.trim()) onOpenBelow(item.id)
        return
      }
      // Enter macht auf — und schließt wieder, wenn sie schon offen ist.
      if (editing) onEndEdit()
      else onEdit(item.id)
      return
    }

    if (e.key === 'Escape' && editing) {
      e.preventDefault()
      e.stopPropagation()
      onEndEdit()
      return
    }

    /*
      **Im Schreibmodus nimmt Backspace die leergeräumte Zeile** — das Gegenstück
      zur Enter-Kette, damit sich eine Liste ohne Maus korrigieren lässt. Entf
      tut dasselbe, schickt den Caret aber nach unten. Kein Toast: Das
      ist ein Korrekturschritt beim Tippen, kein Verlust.
    */
    if (editing) {
      // Eine leergeräumte Zeile mit Kindern bleibt: Still ginge sonst ihr ganzer Ast mit (#181).
      if ((e.key === 'Backspace' || e.key === 'Delete') && title === '' && !hasChildren) {
        e.preventDefault()
        e.stopPropagation()
        if (e.key === 'Delete') focusNext(item.id)
        else focusPrevious(item.id)
        onRemove(item.id, false)
        return
      }
      /*
        **↑/↓ wechseln die Zeile, ohne den Schreibmodus zu verlassen**
        (Nutzer-Vorgabe, 2026-09-20). In einem einzeiligen Feld gibt es kein Oben
        und Unten, die Tasten sind hier also frei. Am Rand der Liste NICHT
        abfangen: Dort übernimmt die Feldkette und springt zur Notiz oder zur
        offenen Zeile.
      */
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        if (!onStep(item.id, e.key === 'ArrowDown' ? 1 : -1, false)) return
        e.preventDefault()
        e.stopPropagation()
      }
      return
    }

    // ── Ab hier: die Zeile ist ausgewählt, nicht offen. Die Tasten gehören der Liste. ──

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      const direction = e.key === 'ArrowDown' ? 1 : -1
      // Am Rand der Liste NICHT abfangen: Dort übernimmt die Feldkette der
      // Aufgaben-Details und springt weiter zur Notiz oder zur offenen Zeile.
      // Mit Shift greift die Auswahl weiter, wie in der Aufgabenliste.
      if (!onStep(item.id, direction, e.shiftKey)) return
      e.preventDefault()
      e.stopPropagation()
      return
    }

    // Leertaste und Entf gelten der ganzen AUSWAHL — mit einer Zeile ist das
    // dieselbe Wirkung wie zuvor, mit mehreren der Grund für die Auswahl.
    if (e.key === ' ') {
      e.preventDefault()
      e.stopPropagation()
      onToggleSelection()
      return
    }

    // Löschen ist hier ein bewusster Griff auf angefasste Zeilen — mit Rückweg,
    // anders als das Rückwärtstippen in einer offenen.
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault()
      e.stopPropagation()
      onRemoveSelection(e.key === 'Delete' ? 1 : -1)
      return
    }

    /*
      **⌘D an angefassten Zeilen kopiert sie, nicht die Aufgabe** (#209). Nur hier: Beim Schreiben
      in der Zeile — wie im Titel der Aufgabe — läuft die Taste weiter zur Seite und kopiert die
      Aufgabe (Sven, 28.09.2026).
    */
    if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key === 'd') {
      e.preventDefault()
      e.stopPropagation()
      onDuplicate?.(item.id)
      return
    }

    /*
      **Die Kürzel des Aufgaben-Menüs gehen vor** (gemeldet 28.09.2026): ⇧W schrieb ein „W“ ans
      Ende, statt die Wiederholung zu öffnen — wie in der Aufgabenliste gehören diese Buchstaben
      der angefassten Zeile. Jeder andere macht sie weiter auf. Verschieben nicht: Eine
      Unteraufgabe wandert mit ihrer Aufgabe, für sich hat sie „Umhängen“ (#257).
    */
    const target = onMenuShortcut ? taskMenuTargetForKey(e) : null
    if (target && target !== 'move') {
      e.preventDefault()
      e.stopPropagation()
      onMenuShortcut!(item.id, target)
      return
    }
    if (onRestShortcut && TASK_MENU_REST_KEYS.some(key => matchesTaskKey(e, key))) {
      e.preventDefault()
      e.stopPropagation()
      onRestShortcut(item.id)
      return
    }

    /*
      **Tippen macht auf** (Nutzer-Vorgabe, 2026-09-20). Wer nur durchspringt,
      lässt die Zeile zu; wer schreibt, meint sie. Das Zeichen hängt hinten an,
      statt den Titel zu ersetzen — sonst nähme ein Anschlag den ganzen Text.
    */
    const zeichen = typedCharacter(e)
    if (zeichen) {
      e.preventDefault()
      e.stopPropagation()
      // Hier angehängt und nicht im Feld: Solange die Zeile zu ist, ist das Feld
      // `readOnly` und ließe den Anschlag fallen.
      setTitle(bisher => bisher + zeichen)
      onEdit(item.id)
    }
  }

  const commit = () => {
    const trimmed = title.trim()
    if (trimmed === item.title) return
    if (!trimmed) {
      // Mit Kindern kommt der Titel zurück — still gelöscht wäre der ganze Ast weg (#181).
      if (hasChildren) {
        setTitle(item.title)
        return
      }
      // Leergeräumt und geschlossen: Das ist ein Korrekturschritt beim Tippen,
      // kein bewusstes Löschen — deshalb ohne Toast.
      onRemove(item.id, false)
      return
    }
    onRename(item.id, trimmed)
  }

  const style = useMemo<React.CSSProperties>(() => ({
    transform: CSS.Transform.toString(transform),
    transition: transition ?? undefined,
  }), [transform, transition])

  const className = [
    'subtask-row',
    ...treeRowClasses(lines),
    progress && progress.total > 0 ? 'subtask-row--parent' : '',
    item.done ? 'subtask-row--done' : '',
    item.pausedAt != null && !item.done ? 'subtask-row--dormant' : '',
    selected ? 'subtask-row--selected' : '',
    editing ? 'subtask-row--editing' : '',
    // Mit Bild am Zeiger (Herausziehen) hebt nicht die Zeile ab, sondern das Bild.
    isDragging && !away ? 'subtask-row--dragging' : '',
    away ? `subtask-row--${away}` : '',
  ].filter(Boolean).join(' ')

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={className}
      // Die Landung beim Einhängen (#204) sucht die neue Zeile darüber.
      data-subtask-id={item.id}
      onDoubleClick={() => onEdit(item.id)}
      // `listeners` ja, `attributes` NEIN: Die setzen `role="button"` und
      // `tabIndex` auf einen Kasten, der ein Textfeld enthält — das wäre eine
      // Lüge über seine Rolle und ein zusätzlicher Tab-Halt in der Feldkette
      // der Aufgaben-Details.
      aria-roledescription={attributes['aria-roledescription']}
      {...listeners}
      /*
        **Nach `listeners`, und deren `onPointerDown` von Hand weitergereicht:**
        dnd-kit bringt selbst eines mit, und das Spread nähme unseres sonst
        stillschweigend weg — die Auswahl bliebe, wo sie war.

        Die Auswahl entscheidet der DRUCK, nicht der Fokus: Nur hier stehen
        Cmd/Strg und Shift zur Verfügung. `onFocus` am Feld fängt bloß den Weg
        über die Feldkette ab (siehe dort).
      */
      onPointerDown={(e) => {
        /*
          **Rechtsklick** (#205): Das Menü meint die Auswahl, wenn die Zeile dazugehört. Kein Ziehen,
          und der Merker nur, wenn gleich ein Fokus kommt — das Loslassen landet schon im Menü.
        */
        if (e.button !== 0) {
          if (document.activeElement !== inputRef.current) pointerDecided.current = true
          onSelect(item.id, { toggle: false, range: false, context: true })
          return
        }
        pointerDecided.current = true
        onSelect(item.id, { toggle: e.metaKey || e.ctrlKey, range: e.shiftKey, press: true })
        listeners?.onPointerDown?.(e)
      }}
      /*
        **Ein Druck neben das Feld (Rand, Marken) hält den Fokus in der Zeile** (gemeldet
        28.09.2026). Sonst fiel er auf `body`, der Blur der Liste räumte die Auswahl ab, und von
        einer Mehrfachauswahl ging beim Ziehen nur die gegriffene Zeile mit.
      */
      onMouseDown={(e) => {
        if ((e.target as HTMLElement).closest('input, button')) return
        e.preventDefault()
        inputRef.current?.focus()
      }}
      // Kam kein Fokuswechsel (die Zeile hatte ihn schon), räumt der Merker hier ab.
      onPointerUp={() => {
        pointerDecided.current = false
        onRelease(item.id)
      }}
    >
      <SubtaskTreeLines lines={lines} />
      <TaskCheck
        checked={item.done}
        onCheckedChange={() => onToggle(item.id)}
        kind="subtask"
        dormant={item.pausedAt != null}
        // #217: Ein Teil der Kinder ist erledigt — dieselben Zahlen wie der Unteraufgaben-Fortschritt hinter dem Titel.
        indeterminate={(progress?.done ?? 0) > 0}
        // Mit Belohnung bringt der Geschafft-Moment seinen eigenen Klang mit (wie an der Aufgabe).
        silent={!asked || reward != null}
        spark={asked || sparkDelay !== null}
        sparkDelay={asked ? 0 : (sparkDelay ?? 0)}
      />
      {/* **Beim Schreiben keine Marken** (Sven, 04.10.2026): Die Zeile gehört dem Titel, das Feld
          füllt die Breite. Auch der Pfeil geht — ein Klick darauf verließe die Zeile mitten im Satz.
          `initial={false}`: Beim Aufbau der Liste stehen die Marken einfach da. */}
      <AnimatePresence initial={false}>
        {!editing && item.attention && (
          <motion.span key="attention" className="subtask-row__attention" {...markGrow}>
            <TaskAttentionBadge level={item.attention} />
          </motion.span>
        )}
        {/* **Dieselben Plätze wie an der Aufgabe** (#219): Aufmerksamkeit und Start vor dem Titel,
            Belohnung bis Tags dahinter, Wiederholung, Ende und Dauer rechts (#283). Nur Anzeige — gesetzt wird über
            Menü und Details. Leere Kästen blenden sich aus (`:empty`). */}
        {!editing && (
          <motion.span key="lead" className="subtask-row__lead" {...markGrow}>
            {item.startDate && <TaskStartDateBadge date={item.startDate} period={item.startPeriod} locale={i18n.language} />}
          </motion.span>
        )}
      </AnimatePresence>
      <input
        ref={inputRef}
        className="subtask-row__title"
        type="text"
        value={title}
        /*
          Immer ein Feld, auch wenn niemand darin schreibt: Der Fokus IST die
          Auswahl. Damit bleiben die Feldkette der Aufgaben-Details
          (`fieldChain` sucht `.subtask-row__title`) und der Weg des
          Carets nach dem Löschen einer Zeile, wie sie waren.
        */
        readOnly={!editing}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={handleKeyDown}
        /*
          Nur der Weg über die **Feldkette** landet hier ohne Druck: ↓ aus der
          Notiz fokussiert die Zeile, und das soll sie anfassen.
        */
        onFocus={() => {
          if (pointerDecided.current) { pointerDecided.current = false; return }
          onSelect(item.id)
        }}
        /*
          **Nur schreiben, nicht schließen.** Das Beenden gehört dem Blur am
          Listen-Kasten (der weiß, wohin der Fokus geht) und `handleSelect` (eine
          andere Zeile anzufassen schließt diese). Stünde es hier, nähme es der
          Übergabe an die Nachbarzeile beim Löschen den Schreibmodus gleich
          wieder weg.
        */
        onBlur={commit}
        spellCheck={false}
      />
      <AnimatePresence initial={false}>
        {/* **Fortschritt der direkten Kinder** (#211): Ring und Zahl, direkt hinter dem Titel. */}
        {!editing && progress && progress.total > 0 && (
          <motion.span key="progress" className="subtask-row__slot" {...markGrow}>
            <BranchProgress className="subtask-row__progress" done={progress.done} total={progress.total} />
          </motion.span>
        )}
        {!editing && (
          <motion.span key="marks" className="subtask-row__marks" {...markGrow}>
            {/* Die eigene Belohnung (#205): klein, ohne Knopf. */}
            {reward && (
              <WithTooltip content={rewardCta(reward, language)}>
                <span className="subtask-row__reward" data-reward-id={reward.id}>
                  <RewardCatalogIcon rewardId={reward.id} name="" size={20} />
                </span>
              </WithTooltip>
            )}
            {/* Glocke (nur eigene Erinnerungen) und Kommentare (#183) — ob sie etwas zeigen, wissen erst sie. */}
            <ReminderMark labels={reminderLabels} onOpen={onOpenReminders ? () => onOpenReminders(item.id) : undefined} />
            <CommentMark count={commentCount} onOpen={onOpenComments ? () => onOpenComments(item.id) : undefined} />
            {item.tags && item.tags.length > 0 && <SubtaskRowTags ids={item.tags} />}
          </motion.span>
        )}
        {!editing && (
          <motion.span key="timings" className="subtask-row__timings" {...markGrow}>
            {/* Die eigene Wiederholung (#205) — rechts vor Ende und Dauer wie an der Aufgabe (#283). Allein
                trägt sie den Rhythmus in Kurzform, der volle Satz steht im Tooltip. */}
            {item.recurrence && (
              <WithTooltip content={describeRecurrence(item.recurrence, t, i18n.language)}>
                <span className="subtask-row__recurrence" data-recurring="">
                  <RecurringIcon size={16} />
                  {!item.dueDate && !item.estimatedDuration && (
                    <span className="subtask-row__recurrence-text">{shortRecurrenceLabel(item.recurrence, t, i18n.language)}</span>
                  )}
                </span>
              </WithTooltip>
            )}
            {item.dueDate && (
              <TaskDeadlineBadge value={{ date: item.dueDate, time: item.dueTime ?? null, period: item.duePeriod ?? null }} locale={i18n.language} />
            )}
            {/* Ohne Fokuszeit: Eine Unteraufgabe bucht keine eigene. */}
            {item.estimatedDuration ? (
              <TaskDurationBadge minutes={item.estimatedDuration} mode={item.durationMode ?? 'duration'} />
            ) : null}
          </motion.span>
        )}
        {/* In einer Hülle: Die Bewegung führt den rechten Rand, der Knopf behält seinen eigenen. */}
        {!editing && onOpen && (
          <motion.span key="open" className="subtask-row__open-slot" {...markGrow}>
            <button
              type="button"
              className="subtask-row__open"
              aria-label={t('taskDetails.openSubtask', { title: item.title })}
              // Der Druck gehört dem Knopf, nicht der Auswahl oder dem Ziehen der Zeile.
              onPointerDown={e => e.stopPropagation()}
              onClick={() => onOpen(item.id)}
            >
              <ChevronRight size={16} />
            </button>
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  )
}
